// src/features/chat/lib/useChatMessages.js
import { useEffect, useState, useCallback, useRef } from "react";
import { io } from "socket.io-client";

export function useChatMessages(productId, user) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const token = localStorage.getItem("token");
  const socketRef = useRef(null);

  // WebSocket
  useEffect(() => {
    if (!productId) return;
    if (socketRef.current) return;

    socketRef.current = io("https://woh-crm.worldofheels.com.ua", {
      path: "/v1/wss",
      transports: ["websocket"],
    });

    socketRef.current.on("connect", () => {
      console.log("✅ Connected to socket");
      socketRef.current.emit("joinProduct", productId);
    });

    socketRef.current.on("newMessage", (msg) => {
      console.log("📩 newMessage", msg);
      setMessages((prev) => [...prev, msg]);
    });

    socketRef.current.on("removeMessage", ({ messageId }) => {
      setMessages((prev) => prev.filter((m) => m._id !== messageId));
    });

    return () => {
      socketRef.current?.disconnect();
      socketRef.current = null;
    };
  }, [productId]);

  const loadMessages = useCallback(
    async (pageToLoad = 1) => {
      if (!productId || (!hasMore && pageToLoad > 1)) return;

      try {
        setLoading(true);
        const headers = { Accept: "application/json" };
        if (token) headers.Authorization = `Bearer ${token}`;

        const res = await fetch(
          `https://woh-crm.worldofheels.com.ua/v1/messages/products/${productId}/messages?page=${pageToLoad}&limit=20`,
          { headers }
        );
        const data = await res.json();

        if (pageToLoad === 1) {
          setMessages(data.items.reverse());
        } else {
          setMessages((prev) => [...data.items.reverse(), ...prev]);
        }

        setHasMore(Boolean(data.hasMore));
        setPage(pageToLoad);
      } finally {
        setLoading(false);
      }
    },
    [productId, token, hasMore]
  );

  useEffect(() => {
    setMessages([]);
    setPage(1);
    setHasMore(true);
    loadMessages(1);
  }, [productId]);

  const sendMessage = useCallback(
    async (text) => {
      const optimisticMessage = {
        _id: `tmp-${Date.now()}`,
        text,
        user,
        createdAt: new Date().toISOString(),
        pending: true,
      };

      const res = await fetch(
        `https://woh-crm.worldofheels.com.ua/v1/messages/products/${productId}/messages`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ text }),
        }
      );
      const data = await res.json();

      if (data?.messageDoc) {
        setMessages((prev) =>
          prev.map((m) => (m._id === optimisticMessage._id ? data.messageDoc : m))
        );
      }
      return data;
    },
    [productId, token, user]
  );

  return { messages, loading, sendMessage, loadMessages, page, hasMore };
}
